/* eslint-disable react-refresh/only-export-components */
import React, { useMemo } from 'react';
import { createCtx } from '../../../../../components/Contexts/createCtx';
import { useTimeUIConfig } from '../../../../../hooks/queries/useTimeUIConfig';
import { useLocalStorage } from '../../../../../hooks/useLocalStorageNew';

const GRID_COLUMN_GAP = 10;

export type GridLayoutContext = {
    gap: number;
    columnWidths: number[];
    totalWidth: number;
    gridColumnTemplate: string;
    gridRowStyles: React.CSSProperties;
    rowHeaderWidth: number;
    setRowHeaderWidth: React.Dispatch<React.SetStateAction<number>>;
};

const [useCtx, Provider] = createCtx<GridLayoutContext>();

export const useGridLayoutContext = useCtx;

export const GridLayoutContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { config } = useTimeUIConfig();
    const [rowHeaderWidth, setRowHeaderWidth] = useLocalStorage('project-approval-row-header-width', 400);

    const columnWidths = useMemo(() => {
        const { useRates } = config?.approval?.projectManagerApproval || {};
        return [
            rowHeaderWidth, // Name/Task Name
            100, // Hours
            150, // Billable (hrs)
            120, // Date
            useRates && 100, // Rate (conditional)
            useRates && 280, // Activity (conditional)
            200, // Status
            200, // Comment - minimum width
        ].filter(Boolean);
    }, [config?.approval?.projectManagerApproval, rowHeaderWidth]);

    const totalWidth = useMemo(() => columnWidths.reduce((acc, width) => acc + width + GRID_COLUMN_GAP, 0), [columnWidths]);

    const gridColumnTemplate = useMemo(() => {
        const template = columnWidths.slice(0, -1).reduce((acc, width) => acc + `${width}px `, '');
        // Last column (Comment) uses 1fr to fill remaining space
        return template + '1fr';
    }, [columnWidths]);

    const gridRowStyles = useMemo(
        (): React.CSSProperties => ({
            display: 'grid',
            gridTemplateColumns: gridColumnTemplate,
            gap: GRID_COLUMN_GAP,
            alignItems: 'center',
            width: '100%',
        }),
        [gridColumnTemplate],
    );

    const context = useMemo((): GridLayoutContext => {
        return {
            columnWidths,
            gap: GRID_COLUMN_GAP,
            gridColumnTemplate,
            gridRowStyles,
            totalWidth,
            rowHeaderWidth,
            setRowHeaderWidth,
        };
    }, [columnWidths, gridColumnTemplate, gridRowStyles, rowHeaderWidth, setRowHeaderWidth, totalWidth]);

    return <Provider value={context}>{children}</Provider>;
};
